import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { ImageIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

interface CmsImageProps {
    src: string | null
    alt: string | null
    sizes: string
    preload?: boolean
    className?: string
}

// Images stored on the site's own CDN are resized by Next.js (see images.remotePatterns in next.config.ts).
const OPTIMIZED_HOST = /(^|\.)cdn\.modulify\.website$/i

const canOptimize = (src: string): boolean => {
    try {
        const url = new URL(src)

        return url.protocol === 'https:' && OPTIMIZED_HOST.test(url.hostname)
    }
    catch {
        return false
    }
}

/**
 * Fills its parent (which must be `relative` and sized) with an image picked in the CMS.
 * Falls back to a plain <img> for any other host and to a neutral tile when no image is set.
 */
export const CmsImage: FC<CmsImageProps> = ({
    src,
    alt,
    sizes,
    preload = false,
    className
}): ReactNode => {
    if (!src) {
        return (
            <div className={cn('absolute inset-0 flex items-center justify-center bg-secondary text-secondary-foreground/40', className)} aria-hidden='true'>
                <ImageIcon className='size-8' />
            </div>
        )
    }

    if (canOptimize(src)) {
        return (
            <Image
                src={src}
                alt={alt ?? ''}
                fill
                sizes={sizes}
                preload={preload}
                className={cn('object-cover', className)}
            />
        )
    }

    return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
            src={src}
            alt={alt ?? ''}
            loading={preload ? 'eager' : 'lazy'}
            decoding='async'
            className={cn('absolute inset-0 size-full object-cover', className)}
        />
    )
}
